import { BrandBottomSheetModal } from "@/components/bottom-sheet-modal";
import { ThemedText } from "@/components/themed-text";
import { DropdownTrigger } from "@/components/ui/dropdown-trigger";
import { VehicleRegistrationInput } from "@/components/ui/vehicle-registration-input";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import {
  FUEL_TYPES,
  TRANSMISSION_OPTIONS,
  VEHICLE_CATEGORIES,
  VEHICLE_COLOURS,
  VEHICLE_MAKES,
  VEHICLE_TYPES,
  VEHICLE_YEARS,
  WHEELBASE_OPTIONS,
  getBodyTypesForVehicleType,
  getModelsForMake,
} from "@/data/vehicle";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { useVehicleCheck } from "@/hooks/useVehicleCheck";
import { BookingStep1Data, bookingStep1Schema } from "@/lib/validation/booking";
import { prefetchPassengerLocation } from "@/lib/utils/passenger-location";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { zodResolver } from "@hookform/resolvers/zod";
import { router } from "expo-router";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { StyleSheet, View } from "react-native";
import { HelperText, Switch } from "react-native-paper";

export type VehicleRegistrationHandle = {
  submit: () => void;
};

export const VehicleRegistration = forwardRef<
  VehicleRegistrationHandle,
  { route?: boolean }
>(function VehicleRegistration({ route = true }, ref) {
  const { theme } = useAuth();
  const desktop = useDesktopLayout();
  const { formData, setFormData, hasReg, setHasReg, setCurrentStep } =
    useCreateRequestStore();

  const vehicleTypeSheetRef = useRef<BottomSheetModal>(null);
  const bodyTypeSheetRef = useRef<BottomSheetModal>(null);
  const transmissionSheetRef = useRef<BottomSheetModal>(null);
  const wheelsbaseSheetRef = useRef<BottomSheetModal>(null);
  const categorySheetRef = useRef<BottomSheetModal>(null);
  const makeSheetRef = useRef<BottomSheetModal>(null);
  const modelSheetRef = useRef<BottomSheetModal>(null);
  const yearSheetRef = useRef<BottomSheetModal>(null);
  const colourSheetRef = useRef<BottomSheetModal>(null);
  const fuelSheetRef = useRef<BottomSheetModal>(null);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BookingStep1Data>({
    resolver: zodResolver(bookingStep1Schema) as any,
    defaultValues: {
      vehicle_registration: formData.vehicle_registration || undefined,
      vehicle_type: formData.vehicle_type || undefined,
      vehicle_category: formData.vehicle_category || undefined,
      vehicle_body_type: formData.vehicle_body_type || undefined,
      vehicle_make: formData.vehicle_make || undefined,
      vehicle_model: formData.vehicle_model || undefined,
      vehicle_colour: formData.vehicle_colour || undefined,
      vehicle_fuel_type: formData.vehicle_fuel_type || undefined,
      vehicle_year: formData.vehicle_year || undefined,
      vehicle_weight: formData.vehicle_weight || undefined,
      vehicle_transmission: formData.vehicle_transmission || undefined,
      vehicle_wheelbase: formData.vehicle_wheelbase || undefined,
    },
  });

  const vehicleRegistration = watch("vehicle_registration");
  const vehicleType = watch("vehicle_type");
  const vehicleMake = watch("vehicle_make");
  const vehicleBodyType = watch("vehicle_body_type");
  const vehicleTransmission = watch("vehicle_transmission");
  const vehicleWheelbase = watch("vehicle_wheelbase");
  const vehicleYear = watch("vehicle_year");
  const vehicleColour = watch("vehicle_colour");
  const vehicleFuelType = watch("vehicle_fuel_type");

  // ─── Derived options ──────────────────────────────────────────────────────
  const bodyTypeOptions = useMemo(
    () => getBodyTypesForVehicleType(vehicleType ?? ""),
    [vehicleType],
  );

  const modelOptions = useMemo(
    () => getModelsForMake(vehicleMake ?? ""),
    [vehicleMake],
  );

  // ─── Labels helpers ───────────────────────────────────────────────────────
  const getLabelFor = (
    options: { value: string; label: string }[],
    value?: string,
  ) => options.find((o) => o.value === value)?.label;

  const vehicleTypeLabel = getLabelFor(VEHICLE_TYPES, vehicleType);
  const bodyTypeLabel = getLabelFor(bodyTypeOptions, vehicleBodyType);
  const transmissionLabel = getLabelFor(
    TRANSMISSION_OPTIONS,
    vehicleTransmission,
  );
  const wheelsbaseLabel = getLabelFor(WHEELBASE_OPTIONS, vehicleWheelbase);
  const makeLabel = getLabelFor(VEHICLE_MAKES, vehicleMake);
  const modelLabel = getLabelFor(modelOptions, watch("vehicle_model"));
  const yearLabel = vehicleYear ? String(vehicleYear) : undefined;
  const colourLabel =
    getLabelFor(VEHICLE_COLOURS, vehicleColour) ??
    (vehicleColour
      ? vehicleColour.charAt(0).toUpperCase() + vehicleColour.slice(1)
      : undefined);
  const fuelLabel =
    getLabelFor(FUEL_TYPES, vehicleFuelType) ??
    (vehicleFuelType
      ? vehicleFuelType.charAt(0).toUpperCase() + vehicleFuelType.slice(1)
      : undefined);

  // ─── Vehicle reg auto-fill ────────────────────────────────────────────────
  const {
    vehicleCheckData,
    checkingVehicle,
    vehicleCheckError,
    vehicleCategory,
  } = useVehicleCheck(vehicleRegistration || "");
  const [isEditingRegistration, setIsEditingRegistration] = useState(false);

  if (!vehicleCheckData && isEditingRegistration) {
    setIsEditingRegistration(false);
  }

  useEffect(() => {
    void prefetchPassengerLocation();
  }, []);

  useEffect(() => {
    if (vehicleCheckData) {
      setValue("vehicle_category", vehicleCheckData?.vehicle_category as any);
      setValue(
        "vehicle_body_type",
        vehicleCheckData?.body_type?.toLowerCase() as string,
      );
      setValue("vehicle_make", vehicleCheckData?.make?.toLowerCase() as string);
      setValue(
        "vehicle_model",
        vehicleCheckData?.model?.toLowerCase() as string,
      );
      setValue(
        "vehicle_colour",
        vehicleCheckData?.colour?.toLowerCase() as string,
      );
      setValue(
        "vehicle_fuel_type",
        vehicleCheckData?.fuel_type?.toLowerCase() as string,
      );
      setValue("vehicle_year", vehicleCheckData?.year as number);
      setValue(
        "vehicle_transmission",
        vehicleCheckData?.transmission?.toLowerCase() as string,
      );
      setValue("vehicle_wheelbase", vehicleCheckData?.wheel_base as string);
      setValue(
        "vehicle_type",
        vehicleCheckData?.class?.toLowerCase() as string,
      );
      setValue(
        "vehicle_weight",
        vehicleCheckData?.weight?.toString() as string,
      );
      setValue(
        "vehicle_image_path",
        vehicleCheckData?.vehicle_image_url as string || undefined,
      );
      setValue(
        "vehicle_mot_status",
        vehicleCheckData?.mot_status?.toLowerCase() as string || undefined,
      );
      setValue(
        "vehicle_expiry_date",
        vehicleCheckData?.mot_expiry_date as string || undefined,
      );
      setValue(
        "vehicle_tax_status",
        vehicleCheckData?.tax_status?.toLowerCase() as string || undefined,
      );
      setValue(
        "vehicle_tax_due_date",
        vehicleCheckData?.tax_due_date as string || undefined,
      );
    }
  }, [vehicleCheckData, setValue]);

  // When vehicle type changes, reset body type
  const handleVehicleTypeSelect = useCallback(
    (value: string) => {
      setValue("vehicle_type", value);
      setValue("vehicle_body_type", "" as any);
      vehicleTypeSheetRef.current?.dismiss();
    },
    [setValue],
  );

  // When make changes, reset model
  const handleMakeSelect = useCallback(
    (value: string) => {
      setValue("vehicle_make", value);
      setValue("vehicle_model", "" as any);
      makeSheetRef.current?.dismiss();
    },
    [setValue],
  );

  const onSubmit = useCallback(
    (data: BookingStep1Data) => {
      if (vehicleRegistration && !vehicleCheckData) return;
      setFormData(data);
      setCurrentStep(2);
      if (route) {
        router.push("/customer/create-request/step2");
      }
      reset();
    },
    [vehicleRegistration, vehicleCheckData, setFormData, reset, route, setCurrentStep],
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => handleSubmit(onSubmit)(),
    }),
    [handleSubmit, onSubmit],
  );

  // ─── Open helpers ─────────────────────────────────────────────────────────
  const openVehicleTypeSheet = useCallback(
    () => vehicleTypeSheetRef.current?.present(),
    [],
  );
  const openBodyTypeSheet = useCallback(
    () => bodyTypeSheetRef.current?.present(),
    [],
  );
  const openTransmissionSheet = useCallback(
    () => transmissionSheetRef.current?.present(),
    [],
  );
  const openWheelbaseSheet = useCallback(
    () => wheelsbaseSheetRef.current?.present(),
    [],
  );
  const openMakeSheet = useCallback(() => makeSheetRef.current?.present(), []);
  const openModelSheet = useCallback(() => {
    if (!vehicleMake) return;
    modelSheetRef.current?.present();
  }, [vehicleMake]);

  const desktopPanelStyle = [
    styles.desktopPanel,
    { backgroundColor: theme.card, borderColor: theme.border },
  ];

  const manualEntryToggle = (
    <View
      style={[
        styles.manualEntryCard,
        desktop
          ? desktopPanelStyle
          : { backgroundColor: theme.background, shadowColor: theme.text },
      ]}
    >
      <View style={{ flex: 1 }}>
        <ThemedText type="smallSemiBold">
          Enter vehicle details manually
        </ThemedText>
        <ThemedText type="small" style={{ color: BrandColors.mediumGray }}>
          Enter details yourself
        </ThemedText>
      </View>
      <Switch
        testID="manual-entry-toggle"
        value={hasReg}
        onValueChange={() => {
          reset();
          setHasReg(!hasReg);
        }}
        color={BrandColors.success}
        style={{ transform: [{ scaleX: 1.3 }, { scaleY: 1.3 }] }}
      />
    </View>
  );

  const renderRegistrationInput = (part: "all" | "input") => (
    <Controller
      control={control}
      name="vehicle_registration"
      render={({ field: { value, onChange } }) => (
        <View>
          <VehicleRegistrationInput
            registration={value}
            onChangeRegistration={onChange}
            isChecking={checkingVehicle}
            error={vehicleCheckError}
            vehicleData={vehicleCheckData}
            vehicleCategory={vehicleCategory}
            part={part}
            {...(desktop
              ? {
                  isEditing: isEditingRegistration,
                  onEditingChange: setIsEditingRegistration,
                }
              : {})}
          />
          {errors.vehicle_registration && (
            <HelperText type="error" style={{ fontSize: 8 }}>
              {errors.vehicle_registration?.message}
            </HelperText>
          )}
        </View>
      )}
    />
  );

  const registrationInput = renderRegistrationInput("all");
  const showDesktopRegistrationInput =
    !vehicleCheckData || isEditingRegistration;

  const desktopVehicleDetails = (
    <VehicleRegistrationInput
      registration={vehicleRegistration}
      onChangeRegistration={(text) => setValue("vehicle_registration", text)}
      isChecking={checkingVehicle}
      error={vehicleCheckError}
      vehicleData={vehicleCheckData}
      vehicleCategory={vehicleCategory}
      part="details"
      isEditing={isEditingRegistration}
      onEditingChange={setIsEditingRegistration}
    />
  );

  const manualFields = (
    <View style={{ gap: 12 }}>
      {/* Vehicle Type dropdown */}
      <DropdownTrigger
        label="Vehicle Type"
        value={vehicleTypeLabel}
        placeholder="Select vehicle type"
        onPress={openVehicleTypeSheet}
      />

      {/* Body Type dropdown — changes based on Vehicle Type */}
      <DropdownTrigger
        label="Vehicle Body Type"
        value={bodyTypeLabel}
        placeholder={
          vehicleType ? "Select body type" : "Select vehicle type first"
        }
        onPress={openBodyTypeSheet}
        hasError={!!errors.vehicle_body_type}
      />
      {errors.vehicle_body_type && (
        <HelperText type="error">
          {errors.vehicle_body_type?.message}
        </HelperText>
      )}

      {/* Make / Model row */}
      <View style={styles.row}>
        <View style={{ flex: 1 }}>
          <DropdownTrigger
            label="Vehicle Make"
            value={makeLabel}
            placeholder="Select make"
            onPress={openMakeSheet}
            hasError={!!errors.vehicle_make}
          />
          {errors.vehicle_make && (
            <HelperText type="error">{errors.vehicle_make?.message}</HelperText>
          )}
        </View>
        <View style={{ flex: 1 }}>
          <DropdownTrigger
            label="Vehicle Model"
            value={modelLabel}
            placeholder={vehicleMake ? "Select model" : "Select make first"}
            onPress={openModelSheet}
            hasError={!!errors.vehicle_model}
          />
          {errors.vehicle_model && (
            <HelperText type="error">
              {errors.vehicle_model?.message}
            </HelperText>
          )}
        </View>
      </View>

      {/* Year / Colour / Fuel row */}
      <View style={styles.row}>
        <View style={styles.thirdColumn}>
          <DropdownTrigger
            label="Year"
            value={yearLabel}
            placeholder="Year"
            onPress={() => yearSheetRef.current?.present()}
            hasError={!!errors.vehicle_year}
          />
          {errors.vehicle_year && (
            <HelperText type="error">{errors.vehicle_year?.message}</HelperText>
          )}
        </View>
        <View style={styles.thirdColumn}>
          <DropdownTrigger
            label="Colour"
            value={colourLabel}
            placeholder="Colour"
            onPress={() => colourSheetRef.current?.present()}
            hasError={!!errors.vehicle_colour}
          />
          {errors.vehicle_colour && (
            <HelperText type="error">
              {errors.vehicle_colour?.message}
            </HelperText>
          )}
        </View>
        <View style={styles.thirdColumn}>
          <DropdownTrigger
            label="Fuel Type"
            value={fuelLabel}
            placeholder="Fuel"
            onPress={() => fuelSheetRef.current?.present()}
            hasError={!!errors.vehicle_fuel_type}
          />
          {errors.vehicle_fuel_type && (
            <HelperText type="error">
              {errors.vehicle_fuel_type?.message}
            </HelperText>
          )}
        </View>
      </View>

      {/* Transmission dropdown */}
      <DropdownTrigger
        label="Transmission"
        value={transmissionLabel}
        placeholder="Select transmission"
        onPress={openTransmissionSheet}
      />

      {/* Wheelbase dropdown */}
      {vehicleBodyType === "van" && (
        <DropdownTrigger
          label="Wheelbase"
          value={wheelsbaseLabel}
          placeholder="Select wheelbase"
          onPress={openWheelbaseSheet}
        />
      )}

      {/* Vehicle Category dropdown */}
      <DropdownTrigger
        label="Vehicle Category (Weight)"
        value={getLabelFor(VEHICLE_CATEGORIES, watch("vehicle_category"))}
        placeholder="Select vehicle category"
        onPress={() => categorySheetRef.current?.present()}
        hasError={!!errors.vehicle_category}
      />
      {errors.vehicle_category && (
        <HelperText type="error">{errors.vehicle_category?.message}</HelperText>
      )}
    </View>
  );

  return (
    <>
      {desktop ? (
        <View style={styles.desktopRow}>
          <View style={styles.desktopSide}>
            {manualEntryToggle}
            {!hasReg && showDesktopRegistrationInput && (
              <View style={desktopPanelStyle}>
                {renderRegistrationInput("input")}
              </View>
            )}
          </View>
          <View style={styles.desktopMain}>
            {hasReg ? manualFields : desktopVehicleDetails}
          </View>
        </View>
      ) : (
        <>
          {manualEntryToggle}
          <View style={styles.formContainer}>
            {/* ── Reg lookup or manual ── */}
            {!hasReg ? registrationInput : manualFields}
          </View>
        </>
      )}
      {/* ── Vehicle Type BottomSheet ── */}
      <BrandBottomSheetModal
        ref={vehicleTypeSheetRef}
        title="Vehicle Type"
        subtitle="Select the type of vehicle"
        options={VEHICLE_TYPES}
        selectedValue={vehicleType}
        onSelect={handleVehicleTypeSelect}
        onClose={() => vehicleTypeSheetRef.current?.dismiss()}
      />

      {/* ── Body Type BottomSheet ── */}
      <BrandBottomSheetModal
        ref={bodyTypeSheetRef}
        title="Vehicle Body Type"
        subtitle={
          vehicleType
            ? `Body types for ${vehicleTypeLabel}`
            : "Select a vehicle type first"
        }
        options={bodyTypeOptions}
        selectedValue={vehicleBodyType}
        onSelect={(value) => {
          setValue("vehicle_body_type", value);
          bodyTypeSheetRef.current?.dismiss();
        }}
        onClose={() => bodyTypeSheetRef.current?.dismiss()}
      />

      {/* ── Transmission BottomSheet ── */}
      <BrandBottomSheetModal
        ref={transmissionSheetRef}
        title="Transmission"
        subtitle="Select the transmission type"
        options={TRANSMISSION_OPTIONS}
        selectedValue={vehicleTransmission}
        onSelect={(value) => {
          setValue("vehicle_transmission", value);
          transmissionSheetRef.current?.dismiss();
        }}
        onClose={() => transmissionSheetRef.current?.dismiss()}
      />

      {/* ── Wheelbase BottomSheet ── */}
      <BrandBottomSheetModal
        ref={wheelsbaseSheetRef}
        title="Wheelbase"
        subtitle="Select the vehicle wheelbase"
        options={WHEELBASE_OPTIONS}
        selectedValue={vehicleWheelbase}
        onSelect={(value) => {
          setValue("vehicle_wheelbase", value);
          wheelsbaseSheetRef.current?.dismiss();
        }}
        onClose={() => wheelsbaseSheetRef.current?.dismiss()}
      />

      {/* ── Vehicle Make BottomSheet ── */}
      <BrandBottomSheetModal
        ref={makeSheetRef}
        title="Vehicle Make"
        subtitle="Select the manufacturer"
        options={VEHICLE_MAKES}
        selectedValue={vehicleMake}
        onSelect={handleMakeSelect}
        searchable={true}
        searchPlaceholder="Search make..."
        onClose={() => makeSheetRef.current?.dismiss()}
      />

      {/* ── Vehicle Model BottomSheet ── */}
      <BrandBottomSheetModal
        ref={modelSheetRef}
        title="Vehicle Model"
        subtitle={makeLabel ? `Models for ${makeLabel}` : "Select a make first"}
        options={modelOptions}
        selectedValue={watch("vehicle_model")}
        onSelect={(value) => {
          setValue("vehicle_model", value);
          modelSheetRef.current?.dismiss();
        }}
        searchable={true}
        searchPlaceholder="Search model..."
        onClose={() => modelSheetRef.current?.dismiss()}
      />

      {/* ── Year BottomSheet ── */}
      <BrandBottomSheetModal
        ref={yearSheetRef}
        title="Year"
        subtitle="Select the vehicle year"
        options={VEHICLE_YEARS}
        selectedValue={yearLabel}
        onSelect={(value) => {
          setValue("vehicle_year", Number(value));
          yearSheetRef.current?.dismiss();
        }}
        onClose={() => yearSheetRef.current?.dismiss()}
      />

      {/* ── Colour BottomSheet ── */}
      <BrandBottomSheetModal
        ref={colourSheetRef}
        title="Colour"
        subtitle="Select the vehicle colour"
        options={VEHICLE_COLOURS}
        selectedValue={vehicleColour}
        onSelect={(value) => {
          setValue("vehicle_colour", value);
          colourSheetRef.current?.dismiss();
        }}
        onClose={() => colourSheetRef.current?.dismiss()}
      />

      {/* ── Fuel Type BottomSheet ── */}
      <BrandBottomSheetModal
        ref={fuelSheetRef}
        title="Fuel Type"
        subtitle="Select the fuel type"
        options={FUEL_TYPES}
        selectedValue={vehicleFuelType}
        onSelect={(value) => {
          setValue("vehicle_fuel_type", value);
          fuelSheetRef.current?.dismiss();
        }}
        onClose={() => fuelSheetRef.current?.dismiss()}
      />

      {/* ── Vehicle Category BottomSheet ── */}
      <BrandBottomSheetModal
        ref={categorySheetRef}
        title="Vehicle Category"
        subtitle="Select the vehicle weight category"
        options={VEHICLE_CATEGORIES}
        selectedValue={watch("vehicle_category")}
        onSelect={(value) => {
          setValue("vehicle_category", value as any);
          categorySheetRef.current?.dismiss();
        }}
        onClose={() => categorySheetRef.current?.dismiss()}
      />
    </>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 40,
    paddingTop: 10,
  },
  formContainer: {
    gap: 14,
  },
  manualEntryCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 4,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 3,
  },
  desktopRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 24,
  },
  desktopSide: {
    width: 360,
    gap: 12,
  },
  desktopMain: {
    flex: 1,
  },
  desktopPanel: {
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 0,
    shadowOpacity: 0,
    elevation: 0,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    justifyContent: "space-between",
  },
  thirdColumn: {
    flex: 1,
  },
  // ── Kept for potential reuse ──────────────────────────────────────────
  button: {
    backgroundColor: BrandColors.primary,
    borderRadius: 8,
    padding: 12,
    alignItems: "center",
  },
  helperText: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    marginTop: 8,
    marginBottom: 12,
  },
});
